<template>  
  <v-container fluid>
    <v-dialog v-model="showDialog" scrollable max-width="850px" style="z-index: 1019;">
			<v-skeleton-loader v-if="loading" type="article, actions" />
      <v-card v-if="!loading">
        <v-card-title style="padding: 10px 0 0 10px">
          <v-col cols="12">
            <v-row justify="start" align="center">
              <span style="font-size: 20px">
                {{ !data.id ? $t('wkt.admin.calendar.wizard-window.new_title') : $t('wkt.admin.calendar.wizard-window.edit_title')}}
              </span>
            </v-row>
          </v-col>
        </v-card-title>
        <v-card-text class="pb-3">
          <v-container fluid @wheel="onWheel">
            <v-form ref="form">
              <v-row>
                <v-col cols="12">
                  <v-text-field
                    v-model="data.title"
                    :label="$t('wkt.admin.calendar.wizard-window.title')"
                    :rules="[validationRules.required]"
                    autocomplete="none"
                    maxLength="250"
                    counter="250"
                  />
                </v-col>
                <v-col cols="12" class="py-0">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.step1') }}</h3>
                </v-col>
            <v-row class="pt-4 pl-4">
              <v-col cols="6">
                <p>{{ $t('wkt.admin.calendar.wizard-window.from') }}</p>
                <v-row>
                  <v-col cols="5">
                    <v-select
                      attach
                      :items="availableFromDays"
                      v-model="data.fromDay"
                      :menu-props="{ maxHeight: '300px', offsetY: true }"
                      :rules="[dayRequiredRule('from')]"
                      :label="$t('wkt.admin.calendar.wizard-window.day')"
                    />
                  </v-col>
                  <v-col cols="5">
                    <v-select
                      attach
                      :items="months"
                      v-model="data.fromMonth"
                      :menu-props="{ maxHeight: '300px', offsetY: true }"
                      :rules="[monthRequiredRule('from')]"
                      :label="$t('wkt.admin.calendar.wizard-window.month')"
                      @change="updateDays('from')"
                    />
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6">
                <p>{{ $t('wkt.admin.calendar.wizard-window.to') }}</p>
                <v-row>
                  <v-col cols="5">
                    <v-select
                      attach
                      :items="availableToDays"
                      v-model="data.toDay"
                      :menu-props="{ maxHeight: '300px', offsetY: true }"
                      :rules="[dayRequiredRule('to')]"
                      :label="$t('wkt.admin.calendar.wizard-window.day')"
                    />
                  </v-col>
                  <v-col cols="5">
                    <v-select
                      attach
                      :items="months"
                      v-model="data.toMonth"
                      :menu-props="{ maxHeight: '300px', offsetY: true }"
                      :rules="[monthRequiredRule('to')]"
                      :label="$t('wkt.admin.calendar.wizard-window.month')"
                      @change="updateDays('to')"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            </v-row>
              <v-row>                                
                <v-col cols="12" class="py-0">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.step2') }}</h3>
                </v-col>
                <v-col cols="12" class="pb-1 d-flex flex-row">
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="L"
                    :label="weekdays[1]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="M"
                    :label="weekdays[2]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="X"
                    :label="weekdays[3]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="J"
                    :label="weekdays[4]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="V"
                    :label="weekdays[5]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="S"
                    :label="weekdays[6]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                  <v-checkbox
                    v-model="selectedWeekdays"
                    value="D"
                    :label="weekdays[0]"
                    class="mt-0 mr-2 text-capitalize"
                    hide-details
                  />
                </v-col>
                <v-col cols="12" class="ma-0 mb-2 pa-0 px-4" style="height: 12px; font-size: 12px;">
                  <span class="error--text">{{ weekdaysErrorMessage }}</span>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.step3') }}</h3>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-radio-group 
                    row 
                    v-model="data.dayType" 
                    style="position: relative; top: -15px; max-height: 94px;"
                  >
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.keep')" :value="null" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.workable')" :value="0" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.rest')" :value="1" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.holiday')" :value="2" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.free')" :value="4" />
									</v-radio-group>
                </v-col>
              </v-row>
              <v-row class="mt-0 py-0 px-2">
                <v-col cols="12">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.notes_title') }}</h3>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-radio-group 
                    row 
                    v-model="data.modifyNotes"
                    class="ma-0 pa-0"
                  >
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.deny_modify_notes')" :value="false" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.allow_modify_notes')" :value="true" />
									</v-radio-group>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-textarea
                    v-model="data.notes"
                    :label="$t('wkt.admin.calendar.wizard-window.description')"
                    :disabled="notesDisabled"
                    class="pt-0"
                    autocomplete="none" 
                    auto-grow 
                    rows="1"
                    @wheel="onWheel"
                    maxLength="1000"
                    counter="1000"
                  />
                </v-col>
              </v-row>
              <v-row class="py-0 px-2">
                <v-col cols="12">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.checkin_title') }}</h3>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-radio-group 
                    row 
                    v-model="data.allowCheckin" 
                    class="ma-0 pa-0"
                  >
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.deny_allow_checkin')" :value="-1" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.not_allow_checkin')" :value="0" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.allow_checkin')" :value="1" />
									</v-radio-group>
                </v-col>
              </v-row>
              <v-row v-if="calendar.calendarMode === constants.CALENDAR_STANDARD_CHECKIN" class="py-0 px-2">
                <v-col cols="12">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.workhours_title') }}</h3>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-radio-group 
                    row 
                    v-model="data.modifyWorkHours" 
                    :disabled="modifyWorkHoursDisabled"
                    class="mt-0 mb-2 pa-0"
                  >
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.deny_modify_workhours')" :value="false" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.allow_modify_workhours')" :value="true" />
									</v-radio-group>
                </v-col>
                <v-col cols="12" md="3" sm="6" class="py-0 px-4">
                  <v-text-field
                    v-model="workHoursFormatted"
                    v-mask="'##:##'"
                    placeholder="00:00"
                    :label="$t('wkt.admin.calendar.wizard-window.work_hours')"
                    :disabled="workHoursDisabled"
                    :error-messages="workHoursErrorMessage"
                    class="pt-0"
                    autocomplete="none"
                  />
                </v-col>
              </v-row>
              <v-row v-if="calendar.calendarMode === constants.CALENDAR_ASSISTED_CHECKING" class="py-0 px-2">
                <v-col cols="12" class="mb-2">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.define_activity_segments') }}</h3>
                </v-col>
                <v-col cols="12">
                  <v-row class="py-0 px-1">
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="startTime1Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_start_1')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="startTime1ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="finishTime1Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_finish_1')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="finishTime1ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="py-0 px-1">
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="startTime2Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_start_2')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="startTime2ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="finishTime2Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_finish_2')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="finishTime2ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                  </v-row>
                  <v-row class="py-0 px-1">
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="startTime3Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_start_3')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="startTime3ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                    <v-col cols="3" class="py-0">
                      <v-text-field
                        v-model="finishTime3Formatted"
                        v-mask="'##:##'"
                        placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.activity_finish_3')"
                        :disabled="activitySegmentsDisabled"
                        :error-messages="finishTime3ErrorMessage"
                        class="pt-0"
                        autocomplete="none"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row class="py-0 px-2">
                <v-col cols="12">
                  <h3>{{ $t('wkt.admin.calendar.wizard-window.limit_checkin_hours') }}</h3>
                </v-col>
                <v-col cols="12" class="py-0">
                  <v-radio-group 
                    row 
                    v-model="data.limitCheckin" 
                    class="mt-0 mb-2 pa-0"
                  >
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.keep_constraint')" :value="-1" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.no_apply_constraint')" :value="0" />
										<v-radio :label="$t('wkt.admin.calendar.wizard-window.apply_constraint')" :value="1" />
									</v-radio-group>
                </v-col>
                <v-col cols="3" class="py-0 px-4">
                  <v-text-field
                    v-model="notBeforeFormatted"
                    v-mask="'##:##'"
                    placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.from_hour')"
                    :disabled="limitCheckinDisabled"
                    :error-messages="notBeforeErrorMessage"
                    class="pt-0"
                    autocomplete="none"
                  />
                </v-col>
                <v-col cols="3" class="py-0 px-4">
                  <v-text-field
                    v-model="notAfterFormatted"
                    v-mask="'##:##'"
                    placeholder="00:00"
                        :label="$t('wkt.admin.calendar.wizard-window.to_hour')"
                    :disabled="limitCheckinDisabled"
                    :error-messages="notAfterErrorMessage"
                    class="pt-0"
                    autocomplete="none"
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn v-if="data.id" color="#ff0000" @click="confirmDelete()">
            <v-icon left color="#ffffff">delete</v-icon>
            <span style="color: #ffffff;">
              {{ $t('wkt.admin.common.dialog-buttons.delete').toUpperCase() }}
            </span>
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="#185EA9" text @click="onSave()" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.save").toUpperCase() }}
          </v-btn>
          <v-btn text @click="showDialog = false" style="font-size: 0.9em; font-weight: 400">
            {{ $t("wkt.admin.common.dialog-buttons.cancel").toUpperCase() }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog
      ref='dlgConfirm'
      :title="$t('wkt.admin.calendar.wizard-window.confirm-dialog.title')"
      :message="$t('wkt.admin.calendar.wizard-window.confirm-dialog.text')"
      :titleOk="$t('wkt.admin.common.dialog-buttons.delete')"
      :titleCancel="$t('wkt.admin.common.dialog-buttons.cancel')"
      @confirmed="deleteItem()"
    />  
  </v-container>
</template>

<script>
import BaseModule from "../../common/BaseModule.js";
import ConfirmDialog from "../../common/ConfirmDialog.vue";
import api from '../../../api/worktime.js';

export default {
  name: "CalendarRuleDialog",
  mixins: [BaseModule],
  components: {
    ConfirmDialog
  },
  data() {
    return {
      loading: false,
      showDialog: false,
      calendar: {},
      data: {},
      weekdays: [],
      selectedWeekdays: [],
      weekdaysErrorMessage: '',
      workHoursFormatted: '',
      workHoursErrorMessage: '',
      startTime1Formatted: '',
      startTime1ErrorMessage: '',
      finishTime1Formatted: '',
      finishTime1ErrorMessage: '',
      startTime2Formatted: '',
      startTime2ErrorMessage: '',
      finishTime2Formatted: '',
      finishTime2ErrorMessage: '',
      startTime3Formatted: '',
      startTime3ErrorMessage: '',
      finishTime3Formatted: '',
      finishTime3ErrorMessage: '',
      notBeforeFormatted: '',
      notBeforeErrorMessage: '',
      notAfterFormatted: '',
      notAfterErrorMessage: '',
      monthsErrorMessage: '',
      requiredFieldsErrorMessage: '',
      isFormSubmitted: false,
      addOneDay: false,
      availableFromDays: this.generateDays(),
      availableToDays: this.generateDays(),
      months: [
      { text: this.$t('wkt.admin.common.filters.month_placeholder'), value: null },
        { text: this.$t('wkt.admin.common.month-january'), value: 1 },
        { text: this.$t('wkt.admin.common.month-february'), value: 2 },
        { text: this.$t('wkt.admin.common.month-march'), value: 3 },
        { text: this.$t('wkt.admin.common.month-april'), value: 4 },
        { text: this.$t('wkt.admin.common.month-may'), value: 5 },
        { text: this.$t('wkt.admin.common.month-june'), value: 6 },
        { text: this.$t('wkt.admin.common.month-july'), value: 7 },
        { text: this.$t('wkt.admin.common.month-august'), value: 8 },
        { text: this.$t('wkt.admin.common.month-september'), value: 9 },
        { text: this.$t('wkt.admin.common.month-october'), value: 10 },
        { text: this.$t('wkt.admin.common.month-november'), value: 11 },
        { text: this.$t('wkt.admin.common.month-december'), value: 12 }
      ],
    };
  },
  computed: {
    notesDisabled() {
      return !this.data.modifyNotes;
    },
    modifyWorkHoursDisabled() {
      return this.data.allowCheckin === 0;
    },
    workHoursDisabled() {
      return this.data.allowCheckin === 0 || !this.data.modifyWorkHours;
    },
    activitySegmentsDisabled() {
      return this.data.allowCheckin === 0;
    },
    limitCheckinDisabled() {
      return this.data.limitCheckin === 0;
    },
  },
  methods: {
    onWheel(event) {
      event.stopPropagation();
    },
    open(calendar, data) {
      this.calendar = calendar;
      this.weekdays = this.getWeekdayNames();
      this.weekdaysErrorMessage = '';
      this.workHoursErrorMessage = '';
      this.startTime1ErrorMessage = '';
      this.finishTime1ErrorMessage = '';
      this.startTime2ErrorMessage = '';
      this.finishTime2ErrorMessage = '';
      this.startTime3ErrorMessage = '';
      this.finishTime3ErrorMessage = '';
      this.notBeforeErrorMessage = '';
      this.notAfterErrorMessage = '';
      this.monthsErrorMessage = '';
      this.requiredFieldsErrorMessage = '';
      this.addOneDay = false;

      if (!data) {
        this.data = {
          companyId: calendar.companyId,
          calendarId: calendar.id,
          dayType: null,
          modifyNotes: false,
          allowCheckin: -1,
          modifyWorkHours: false,
          workHours: 0,
          limitCheckin: -1,
          fromDay: null,
          toDay: null,
          fromMonth: null,
          toMonth: null
        };
        
        this.selectedWeekdays = [];
        this.workHoursFormatted = '';
        this.startTime1Formatted = '';
        this.finishTime1Formatted = '';
        this.startTime2Formatted = '';
        this.finishTime2Formatted = '';
        this.startTime3Formatted = '';
        this.finishTime3Formatted = '';
        this.notBeforeFormatted = '';
        this.notAfterFormatted = '';
      } else {
        this.data = data;
        
        this.selectedWeekdays = this.data.weekdays.split(',');
        this.workHoursFormatted = this.data.workHours ? this.convertDoubleToHHMM(this.data.workHours) : null;
        this.startTime1Formatted = this.convertSimpleTimeToHHMM(this.data.startTime1);
        this.finishTime1Formatted = this.convertSimpleTimeToHHMM(this.data.finishTime1);
        this.startTime2Formatted = this.convertSimpleTimeToHHMM(this.data.startTime2);
        this.finishTime2Formatted = this.convertSimpleTimeToHHMM(this.data.finishTime2);
        this.startTime3Formatted = this.convertSimpleTimeToHHMM(this.data.startTime3);
        this.finishTime3Formatted = this.convertSimpleTimeToHHMM(this.data.finishTime3);
        this.notBeforeFormatted = this.convertSimpleTimeToHHMM(this.data.notBefore);
        this.notAfterFormatted = this.convertSimpleTimeToHHMM(this.data.notAfter);
      }

      this.updateDays('from');
      this.updateDays('to');

      this.showDialog = true;
      
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    validateTime(time) {
      if (!time) {
        return { message: this.$t('wkt.admin.common.validation-utils.required_field'), result: false };
      }
      
      if (time.length != 5) {
        return { message: this.$t('wkt.admin.common.validation-utils.invalid_field'), result: false };
      }

      const hours = parseInt(time.substring(0, 2));
      const minutes = parseInt(time.substring(3, 5));

      if (hours > 23 || minutes > 59 || hours == 23 && minutes > 59) {
        return { message: this.$t('wkt.admin.common.validation-utils.invalid_field'), result: false };
      }

      return { message: '', result: true };
    },
    validateWorkHours() {
      return this.validateTime(this.workHoursFormatted);
    },
    validateNotBefore() {
      if (!this.notBeforeFormatted) {
        return { message: '', result: true };
      }
      return this.validateTime(this.notBeforeFormatted);
    },
    validateNotAfter() {
      if (!this.notAfterFormatted) {
        return { message: '', result: true };
      }
      return this.validateTime(this.notAfterFormatted);
    },
    validateActivity(start, finish) {
      if (start) {
        let validation = this.validateTime(start);
        if (!validation.result) {
          validation.message = this.$t('wkt.admin.calendar.wizard-window.enter_correct_period');
          return validation;
        }
      }
      
      if (finish) {
        let validation = this.validateTime(finish);
        if (!validation.result) {
          validation.message = this.$t('wkt.admin.calendar.wizard-window.enter_correct_period');
          return validation;
        }
      }
      
      const startTime = this.convertTimeAsStringToSimpleTime(start);
      const finishTime = this.convertTimeAsStringToSimpleTime(finish);

      let diff = !startTime && !finishTime ? null : 0;

      if (startTime && finishTime) {
        diff = this.getAbsoluteHoursDifference(startTime, finishTime);
      }

      if (diff === null || diff > 0) {
        return { message: '', result: true };
      }

      return { message: this.$t('wkt.admin.calendar.wizard-window.enter_correct_period'), result: false };
    },
    validateActivityOrder(start1, finish1, start2, finish2) {
      const startTime1 = this.convertTimeAsStringToSimpleTime(start1);
      const finishTime1 = this.convertTimeAsStringToSimpleTime(finish1);
      const startTime2 = this.convertTimeAsStringToSimpleTime(start2);
      const finishTime2 = this.convertTimeAsStringToSimpleTime(finish2);

      if (!startTime1 || !finishTime1 || !startTime2 || !finishTime2) {
        return { message: '', result: true };
      }

      const message = this.$t('wkt.admin.calendar.wizard-window.activity_order_error');

      const startDate1 = this.convertSimpleTimeAsDate(startTime1);
      const finishDate1 = this.convertSimpleTimeAsDate(finishTime1);

      if (finishDate1.getTime() < startDate1.getTime() && this.addOneDay) {
        return { message: this.$t('wkt.admin.calendar.wizard-window.night_shift_exists_error'), result: false };
      }

      if (this.addOneDay) {
        startDate1.setDate(startDate1.getDate() + 1);
        finishDate1.setDate(finishDate1.getDate() + 1);
      }

      if (finishDate1.getTime() < startDate1.getTime()) {
        finishDate1.setDate(finishDate1.getDate() + 1);
        this.addOneDay = true;
      }

      const startDate2 = this.convertSimpleTimeAsDate(startTime2);
      const finishDate2 = this.convertSimpleTimeAsDate(finishTime2);

      if (finishDate2.getTime() < startDate2.getTime() && this.addOneDay) {
        return { message: this.$t('wkt.admin.calendar.wizard-window.night_shift_exists_error'), result: false };
      }

      if (this.addOneDay) {
        startDate2.setDate(startDate2.getDate() + 1);
        finishDate2.setDate(finishDate2.getDate() + 1);
      }

      if (finishDate2.getTime() < startDate2.getTime()) {
        finishDate2.setDate(finishDate2.getDate() + 1);
        this.addOneDay = true;
      }

      if (startDate2.getTime() < finishDate1.getTime()) {
        return { message: message, result: false };
      }

      return { message: '', result: true };
    },
    validateActivityOverlap(start1, finish1, start2, finish2) {
      const startTime1 = this.convertTimeAsStringToSimpleTime(start1);
      const finishTime1 = this.convertTimeAsStringToSimpleTime(finish1);
      const startTime2 = this.convertTimeAsStringToSimpleTime(start2);
      const finishTime2 = this.convertTimeAsStringToSimpleTime(finish2);

      if (!startTime1 || !finishTime1 || !startTime2 || !finishTime2) {
        return { message: '', result: true };
      }

      const message = this.$t('wkt.admin.calendar.wizard-window.activity-conflicts_error');

      const startDate1 = this.convertSimpleTimeAsDate(startTime1);
      const finishDate1 = this.convertSimpleTimeAsDate(finishTime1);

      if (this.addOneDay) {
        startDate1.setDate(startDate1.getDate() + 1);
        finishDate1.setDate(finishDate1.getDate() + 1);
      }

      if (finishDate1.getTime() < startDate1.getTime()) {
        finishDate1.setDate(finishDate1.getDate() + 1);
        this.addOneDay = true;
      }

      const startDate2 = this.convertSimpleTimeAsDate(startTime2);
      const finishDate2 = this.convertSimpleTimeAsDate(finishTime2);

      if (this.addOneDay) {
        startDate2.setDate(startDate2.getDate() + 1);
        finishDate2.setDate(finishDate2.getDate() + 1);
      }

      if (finishDate2.getTime() < startDate2.getTime()) {
        finishDate2.setDate(finishDate2.getDate() + 1);
        this.addOneDay = true;
      }

      if (finishDate2.getTime() > startDate1.getTime() && startDate2.getTime() < finishDate1.getTime()) {
        return { message: message, result: false };
      }
      if (finishDate2.getTime() > startDate1.getTime() && startDate2.getTime() < finishDate1.getTime()) {
        return { message: message, result: false };
      }

      if (finishDate1.getTime() > startDate2.getTime() && startDate1.getTime() < finishDate2.getTime()) {
        return { message: message, result: false };
      }
      if (finishDate1.getTime() > startDate2.getTime() && startDate1.getTime() < finishDate2.getTime()) {
        return { message: message, result: false };
      }

      return { message: '', result: true };
    },
    monthRequiredRule(type) {
      return (value) => {
          if (!this.isFormSubmitted) {
              return true;
          }
          if (type === 'from' && this.data.fromDay && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.toDay && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'from' && this.data.toMonth && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.fromMonth && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.fromMonth && this.data.toMonth) {
              if (this.data.toMonth < this.data.fromMonth) {
                  return this.$t('wkt.admin.common.validation-utils.start_date_greater_than_end_date');
              }
          }
          return true;
      };
    },
    dayRequiredRule(type) {
      return (value) => {
          if (!this.isFormSubmitted) {
              return true;
          }
          if (type === 'from' && this.data.fromMonth && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.toMonth && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'from' && this.data.fromDay && !this.data.fromMonth) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.toDay && !this.data.toMonth) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'from' && this.data.toDay && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.fromDay && !value) {
              return this.$t('wkt.admin.common.validation-utils.required_field');
          }
          if (type === 'to' && this.data.fromMonth && this.data.toMonth && this.data.fromMonth === this.data.toMonth) {
              if (this.data.fromDay > this.data.toDay) {
                  return this.$t('wkt.admin.common.validation-utils.start_date_greater_than_end_date');
              }
          }
          return true;
      };
    },
    validate() {
      if (this.selectedWeekdays.length === 0) {
        this.weekdaysErrorMessage = this.$t('wkt.admin.calendar.wizard-window.weekday-required_error');
        return false;
      }
      
      this.weekdaysErrorMessage = '';
      
      let validation = {};

      if (!this.workHoursDisabled) {
        validation = this.validateWorkHours();
        this.workHoursErrorMessage = validation.message;
        if (!validation.result) {
          return false;
        }
      }

      validation = this.validateActivity(this.startTime1Formatted, this.finishTime1Formatted);
      this.startTime1ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateActivity(this.startTime2Formatted, this.finishTime2Formatted);
      this.startTime2ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateActivity(this.startTime3Formatted, this.finishTime3Formatted);
      this.startTime3ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      this.addOneDay = false;

      validation = this.validateActivityOrder(this.startTime1Formatted, this.finishTime1Formatted, 
        this.startTime2Formatted, this.finishTime2Formatted, this.startTime2ErrorMessage);
      this.startTime2ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateActivityOrder(this.startTime1Formatted, this.finishTime1Formatted, 
        this.startTime3Formatted, this.finishTime3Formatted, this.startTime3ErrorMessage);
      this.startTime3ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }
      
      validation = this.validateActivityOrder(this.startTime2Formatted, this.finishTime2Formatted, 
        this.startTime3Formatted, this.finishTime3Formatted, this.startTime3ErrorMessage);
      this.startTime3ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      let monthValidationResult = this.monthRequiredRule('from')(this.data.fromMonth);
      if (!monthValidationResult) {
        this.monthsErrorMessage = monthValidationResult;
        return false;
      }

      monthValidationResult = this.monthRequiredRule('to')(this.data.toMonth);
      if (!monthValidationResult) {
        this.monthsErrorMessage = monthValidationResult;
        return false;
      }

      let dayValidationResult = this.dayRequiredRule('from')(this.data.fromDay);
      if (!dayValidationResult) {
        this.requiredFieldsErrorMessage = dayValidationResult;
        return false;
      }

      dayValidationResult = this.dayRequiredRule('to')(this.data.toDay);
      if (!dayValidationResult) {
        this.requiredFieldsErrorMessage = dayValidationResult;
        return false;
      }

      this.addOneDay = false;

      validation = this.validateActivityOverlap(this.startTime1Formatted, this.finishTime1Formatted, 
        this.startTime2Formatted, this.finishTime2Formatted, this.startTime2ErrorMessage);
      this.startTime2ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }
 
      validation = this.validateActivityOverlap(this.startTime1Formatted, this.finishTime1Formatted, 
        this.startTime3Formatted, this.finishTime3Formatted, this.startTime3ErrorMessage);
      this.startTime3ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateActivityOverlap(this.startTime2Formatted, this.finishTime2Formatted, 
        this.startTime3Formatted, this.finishTime3Formatted, this.startTime3ErrorMessage);
      this.startTime3ErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateNotBefore();
      this.notBeforeErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      validation = this.validateNotAfter();
      this.notAfterErrorMessage = validation.message;
      if (!validation.result) {
        return false;
      }

      return true;
    },
    generateDays(maxDays = 31) {
      return [{ text: this.$t('wkt.admin.common.filters.day_placeholder'), value: null }, ...Array.from({ length: maxDays }, (_, i) => ({ text: i + 1, value: i + 1 }))];
    },
    getDaysInMonth(month) {
      if (!month) return this.generateDays();

      const year = 2024;
      let daysInMonth;

      if ([4, 6, 9, 11].includes(month)) {
          daysInMonth = 30;
      } else if (month === 2) {
          const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
          daysInMonth = isLeapYear ? 29 : 28;
      } else {
          daysInMonth = 31;
      }

      return this.generateDays(daysInMonth);
    },
    updateDays(type) {
      if (type === 'from') {
          const currentMonth = this.data.fromMonth;
          this.availableFromDays = this.getDaysInMonth(currentMonth);

          if (currentMonth) {
              const daysInCurrentMonth = this.availableFromDays.length - 1;
              if (this.data.fromDay > daysInCurrentMonth) {
                  this.data.fromDay = daysInCurrentMonth;
              }
          }
      } else if (type === 'to') {
          const currentMonth = this.data.toMonth;
          this.availableToDays = this.getDaysInMonth(currentMonth);

          if (currentMonth) {
              const daysInCurrentMonth = this.availableToDays.length - 1;
              if (this.data.toDay > daysInCurrentMonth) {
                  this.data.toDay = daysInCurrentMonth;
              }
          }
      }
    },
    onSave() {      
      this.isFormSubmitted = true;
      if (!this.$refs.form.validate()) {
        return;
      }

      if (!this.validate()) {
        return;
      }

      this.data.weekdays = this.selectedWeekdays.toString();      
			this.data.workHours = this.workHoursFormatted ? this.parseHHMMToDouble(this.workHoursFormatted) : null;
			this.data.startTime1 = this.convertTimeAsStringToSimpleTime(this.startTime1Formatted);
			this.data.finishTime1 = this.convertTimeAsStringToSimpleTime(this.finishTime1Formatted);
			this.data.startTime2 = this.convertTimeAsStringToSimpleTime(this.startTime2Formatted);
			this.data.finishTime2 = this.convertTimeAsStringToSimpleTime(this.finishTime2Formatted);
			this.data.startTime3 = this.convertTimeAsStringToSimpleTime(this.startTime3Formatted);
			this.data.finishTime3 = this.convertTimeAsStringToSimpleTime(this.finishTime3Formatted);
			this.data.notBefore = this.convertTimeAsStringToSimpleTime(this.notBeforeFormatted);
			this.data.notAfter = this.convertTimeAsStringToSimpleTime(this.notAfterFormatted);

      this.loading = true;

      api.storeCalendarRule(this.data,
        (res) => {
          this.loading = false;
          this.showDialog = false;
          if (res) {
            this.$emit('refresh');
          }
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    },
    confirmDelete() {
      this.$refs.dlgConfirm.confirm(this.data);
    },
    deleteItem() {
      this.loading = true;

      api.deleteCalendarRuleById(this.data.id,
        () => {
          this.loading = false;
          this.showDialog = false;
          this.$emit("refresh");
        },
        (error) => {
          this.loading = false;
          this.$log.error(error);
          this.$emit("show-alert", { text: error.message, color: "error", timeout: 3000 });
        }
      );
    }
  }
}
</script>